import React, { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Calendar, Clock, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useArticleDetails, RelatedArticle } from "@/hooks/use-article-details";

const RelatedArticleCard = ({
  id,
  title,
  excerpt,
  imageUrl,
  index,
}: RelatedArticle & { index: number }) => (
  <div
    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500 hover:translate-y-[-8px] animate-fade-in opacity-0"
    style={{ animationDelay: `${0.3 + index * 0.1}s` }}
  >
    <div className="h-40 overflow-hidden">
      <img
        src={imageUrl}
        alt={title}
        className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-700"
      />
    </div>
    <div className="p-4">
      <h3 className="text-lg font-bold text-[#1A2B4D] mb-2 line-clamp-2">
        {title}
      </h3>
      <p className="text-[#1A2B4D]/70 text-sm mb-4 line-clamp-2">{excerpt}</p>
      <Link to={`/article?id=${id}`}>
        <Button
          variant="outline"
          className="w-full bg-[#1A2B4D] text-white transition-all duration-300 hover:scale-105 group"
        >
          Read More
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </Button>
      </Link>
    </div>
  </div>
);

const ArticleDetails = () => {
  const { article, relatedArticles, loading, error } = useArticleDetails();
  const location = useLocation();

  // Hiệu ứng reveal nội dung khi scrolling
  useEffect(() => {
    if (!loading && article) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animate-visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      // Select all elements with animation-ready class
      const animatedElements = document.querySelectorAll(".animation-ready");
      animatedElements.forEach((el) => observer.observe(el));

      return () => {
        animatedElements.forEach((el) => observer.unobserve(el));
      };
    }
  }, [loading, article]);

  // Thêm useEffect để scroll lên đầu trang khi articleId hoặc URL thay đổi
  useEffect(() => {
    if (!loading) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location.search]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <div className="container mx-auto px-4 md:px-6 py-24 text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-12"></div>
              <div className="h-64 bg-gray-200 rounded mb-12"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="mt-8 text-[#1A2B4D]/70">Loading article...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <div className="container mx-auto px-4 md:px-6 py-24 text-center">
            <h1
              className="text-3xl font-bold text-[#1A2B4D] mb-4 animate-fade-in opacity-0"
              style={{ animationDelay: "0.1s" }}
            >
              {error || "Article not found"}
            </h1>
            <p
              className="text-[#1A2B4D]/70 mb-8 animate-fade-in opacity-0"
              style={{ animationDelay: "0.2s" }}
            >
              We couldn't find the article you're looking for.
            </p>
            <Link
              to="/articles"
              className="animate-fade-in opacity-0"
              style={{ animationDelay: "0.3s" }}
            >
              <Button className="transition-all duration-300 hover:scale-105">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to articles
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20">
        {/* Article Hero */}
        <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover animate-fade-in opacity-0"
            style={{ animationDelay: "0.1s" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent">
            <div className="container mx-auto px-4 md:px-6 h-full flex items-end pb-12">
              <div className="max-w-3xl">
                <Link
                  to="/articles"
                  className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors animate-fade-in opacity-0"
                  style={{ animationDelay: "0.2s" }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to articles
                </Link>
                <h1
                  className="text-4xl md:text-5xl font-bold text-white mb-4 animate-slide-in-up opacity-0"
                  style={{ animationDelay: "0.3s" }}
                >
                  {article.title}
                </h1>
                <div
                  className="flex items-center gap-4 text-white/80 animate-slide-in-up opacity-0"
                  style={{ animationDelay: "0.4s" }}
                >
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    {article.date}
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    {article.readTime}
                  </div>
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    {article.author}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 md:px-6">
            {/* Change grid layout to flex for centering */}
            <div className="flex justify-center">
              {/* Adjust width and remove column span */}
              <div
                className="w-full lg:w-2/3 animate-fade-in"
                style={{ animationDelay: "0.5s" }}
              >
                <div
                  className="prose prose-lg max-w-none mb-12" // Added margin-bottom
                  dangerouslySetInnerHTML={{ __html: article.content || "" }}
                />

                {/* Moved Category Section */}
                <div
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 animate-fade-in opacity-0" // Removed margin-bottom mb-8 and added animation
                  style={{ animationDelay: "0.6s" }} // Adjusted animation delay
                >
                  <h3 className="font-bold text-[#1A2B4D] mb-2">Category</h3>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/articles?category=${article.categoryId}`}
                      className="inline-block px-4 py-2 bg-[#1A2B4D]/10 text-[#1A2B4D] rounded-full hover:bg-[#1A2B4D] hover:text-white transition-colors"
                    >
                      {article.category}
                    </Link>
                    {article.subCategory && article.subCategoryId && (
                      <Link
                        to={`/articles?category=${article.categoryId}&sub_category=${article.subCategoryId}`}
                        className="inline-block px-4 py-2 bg-gold/10 text-[#1A2B4D] rounded-full hover:bg-gold hover:text-white transition-colors"
                      >
                        {article.subCategory}
                      </Link>
                    )}
                  </div>
                </div>
                {/* End Moved Category Section */}
              </div>

              {/* Removed Sidebar container and Author Card */}
              {/* <div className="lg:col-span-1">
                <div className="sticky top-24"> */}
              {/* Removed Author Card */}
              {/* {article.authorBio && (
                    <div
                      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8 hover:shadow-md transition-all duration-300 animate-fade-in-right opacity-0"
                      style={{ animationDelay: "0.5s" }}
                    >
                      ... Author Card content ...
                    </div>
                  )} */}
              {/* End Removed Author Card */}

              {/* Category was here, now moved */}
              {/* </div>
              </div> */}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4 md:px-6">
              <h2
                className="text-3xl font-bold text-[#1A2B4D] mb-8 animate-fade-in opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                Related articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {relatedArticles.map((article, index) => (
                  <RelatedArticleCard key={index} {...article} index={index} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ArticleDetails;
